import React, { useContext, useState, memo } from 'react';
import { Icon, TertiaryButton, Link } from '@hz-design-system/web-ui';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { currency } from '@ecg-marktplaats/aurora-js-formatters';

import deleteBid from '../../repositories/deleteBid';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';

import { TListingClient } from 'types/TListing';

import Classes from './BiddingList.scss';
type TBiddingListProps = {
  bids: TListingClient['bidsInfo']['bids'];
  isListingReserved: boolean;
  onRemove: Function;
};

const VISIBLE_BIDS = 5;
const MAX_VISIBLE_BIDS = 20;

const isSameUser = (userId, bidderId) => userId === bidderId;

let DeleteBidDialog;

const BiddingList = ({ bids, onRemove, isListingReserved }: TBiddingListProps) => {
  const { date, t } = useI18nContext();
  const {
    xsrfToken,
    userDetails: { id: userId, isAdOwner },
  } = useContext(EnvironmentContext);
  const [isShowMoreButton, setIsShowMoreButton] = useState(Number(bids?.length) > VISIBLE_BIDS);
  const [removeBidId, setRemoveBidId] = useState<number | undefined>();
  const [isDeleteBidDialogLoaded, setDeleteBidDialogLoaded] = useState(false);

  if (!bids?.length) {
    return (
      <div className={Classes.noBidding}>
        <Trans i18nKey="bidding.noBidding" tagName="span" />
      </div>
    );
  }

  const loadDeleteBidDialog = async () => {
    if (!isDeleteBidDialogLoaded) {
      try {
        DeleteBidDialog = (await import(/* webpackChunkName: "DeleteBidDialog" */ './DeleteBidDialog')).default;
        setDeleteBidDialogLoaded(true);
      } catch (err) {}
    }
  };

  const setRemoveBid = (id) => {
    loadDeleteBidDialog();
    setRemoveBidId(id);
  };

  const toggleList = (e) => {
    e.preventDefault();
    setIsShowMoreButton(!isShowMoreButton);
  };

  const handleCancel = () => {
    // eslint-disable-next-line no-undefined
    setRemoveBidId(undefined);
  };

  const handleConfirm = async () => {
    const [{ success }, { default: showToastNotification }] = await Promise.all([
      deleteBid({ bidId: removeBidId, xsrfToken }),
      import(/* webpackChunkName: "toastNotification" */ '../../../../components/Toast/ToastNotification'),
    ]);

    // eslint-disable-next-line no-undefined
    setRemoveBidId(undefined);

    if (success) {
      showToastNotification({
        type: 'success',
        description: isAdOwner ? t('bidding.notifications.sellerBidDeleted') : t('bidding.notifications.deleted'),
      });

      onRemove();
    } else {
      showToastNotification({
        type: 'error',
        description: t('error.generic'),
      });
    }
  };

  return (
    <>
      <div className={Classes.container}>
        {(isShowMoreButton ? bids.slice(0, VISIBLE_BIDS) : bids.slice(0, MAX_VISIBLE_BIDS)).map(
          ({ id, value, user: { nickname, id: bidderId }, date: biddingDate }) => (
            <div
              key={id}
              className={`${Classes.content} ${
                !isListingReserved && isSameUser(userId, bidderId) ? Classes.ownBidContainer : ''
              }`}
            >
              <div className={Classes.name}>{nickname}</div>
              <div className={Classes.price}>{currency(value, true)}</div>
              <div className={Classes.date}>
                {!biddingDate.includes('T')
                  ? date(new Date(parseInt(biddingDate, 10)), "D MMM 'YY")
                  : date(new Date(biddingDate), "D MMM 'YY")}
              </div>
              {!isListingReserved && (isSameUser(userId, bidderId) || isAdOwner) && (
                <div className={Classes.ownBidContent}>
                  <Link onClick={() => setRemoveBid(id)}>
                    <Trans i18nKey={isAdOwner ? 'bidding.seller.removeBid' : 'bidding.removeBid'} tagName="span" />
                  </Link>
                </div>
              )}
            </div>
          ),
        )}
      </div>

      {isShowMoreButton && (
        <div className={Classes.showMore}>
          <TertiaryButton onClick={toggleList}>
            <Icon name="arrow-down" color="text-link-default" className={Classes.linkIcon} />
            <Trans i18nKey="bidding.showMore" tagName="span" />
          </TertiaryButton>
        </div>
      )}

      {isDeleteBidDialogLoaded && removeBidId && <DeleteBidDialog onCancel={handleCancel} onConfirm={handleConfirm} />}
    </>
  );
};

export default memo(BiddingList);
